import { appConfig } from "@/config/app";

export type BillingCycle = "monthly" | "annual";

export type Tier = {
  /** Identifiant interne, stable (liens, analytics). */
  id: string;
  /** Nom public. */
  name: string;
  description: string;
  price: { monthly: number; annual: number } | "custom";
  /** null : selon le besoin (sur devis). */
  users: number | null;
  invoices: number | null;
  /** Mise en demeure payée à l'unité, ou comprise dans l'offre. */
  lateNotice: "unit" | "included";
  cta: { label: string; to: string };
  featured?: boolean;
};

/** Prix d'une mise en demeure dans les offres qui ne la comprennent pas. */
export const LATE_NOTICE_UNIT_PRICE = "14,90\u00a0€";

export const TIERS: Tier[] = [
  {
    id: "free",
    name: "Découverte",
    description: "Pour voir vos premiers clients à relancer.",
    price: { monthly: 0, annual: 0 },
    users: 1,
    invoices: 25,
    lateNotice: "unit",
    cta: { label: "Créer un compte", to: "/signup" },
  },
  {
    id: "starter",
    name: "Essentiel",
    description: "Pour relancer seul, sans y passer la journée.",
    price: { monthly: 19, annual: 15 },
    users: 2,
    invoices: 100,
    lateNotice: "unit",
    cta: { label: "Créer un compte", to: "/signup" },
  },
  {
    id: "pro",
    name: "Pro",
    description: "Pour une petite équipe qui relance chaque semaine.",
    price: { monthly: 59, annual: 47 },
    users: 5,
    invoices: 500,
    lateNotice: "included",
    cta: { label: "Créer un compte", to: "/signup" },
    featured: true,
  },
  {
    id: "business",
    name: "Équipe",
    description: "Pour un poste clients tenu à plusieurs.",
    price: { monthly: 179, annual: 143 },
    users: 15,
    invoices: 3000,
    lateNotice: "included",
    cta: { label: "Créer un compte", to: "/signup" },
  },
];

export const ENTERPRISE: Tier = {
  id: "enterprise",
  name: "Sur mesure",
  description: "Au-delà de ces volumes, ou pour une marque blanche.",
  price: "custom",
  users: null,
  invoices: null,
  lateNotice: "included",
  cta: { label: "Nous écrire", to: `mailto:${appConfig.contactEmail}` },
};

/** Première capacité qui couvre les deux besoins, sans orienter vers une offre plus chère. */
export function recommendTier(users: number, invoices: number): Tier | null {
  if (!Number.isSafeInteger(users) || users < 1 || !Number.isSafeInteger(invoices) || invoices < 0) return null;
  return TIERS.find((tier) => tier.users !== null && tier.invoices !== null && users <= tier.users && invoices <= tier.invoices) ?? ENTERPRISE;
}

/** Ce que chaque offre contient, sans exception. */
export const INCLUDED: string[] = [
  "Synchronisation Pennylane chaque heure, en lecture seule",
  "Relances envoyées depuis votre Gmail, factures en PDF jointes",
  "Liste «\u00a0À relancer\u00a0», échéances proches, groupes par SIREN",
  "Six modèles de relance fournis, tous modifiables",
  "Appels, notes, promesses et réponses gardés sur le client",
  "Mise en demeure préparée, verrouillée sous votre seuil",
  "Mode session pour enchaîner les appels, Aircall en option",
];

/** Nombre lisible, sans coupure de ligne au milieu (« 3 000 »). */
export const count = (n: number) =>
  n.toLocaleString("fr-FR").replace(/\s/g, "\u00a0");

/** « 5 utilisateurs · 500 factures actives », ou le cas sur devis. */
export function capacityLabel(tier: Tier): string {
  if (tier.users === null || tier.invoices === null) {
    return "Utilisateurs et factures selon votre besoin";
  }
  const users = `${tier.users} utilisateur${tier.users > 1 ? "s" : ""}`;
  return `${users} · ${count(tier.invoices)} factures actives`;
}

export function lateNoticeLabel(tier: Tier): string {
  return tier.lateNotice === "unit"
    ? `Mise en demeure\u00a0: ${LATE_NOTICE_UNIT_PRICE} à l’unité`
    : "Mises en demeure comprises";
}

/** Total facturé sur un an au tarif annuel ; null sur devis. */
export function annualTotal(tier: Tier): number | null {
  return tier.price === "custom" ? null : tier.price.annual * 12;
}
